import { CivilDate } from 'tc39-proposal-temporal';
import {
  getCivilDateDayOfWeek,
  getCivilDateMonthStart,
} from '@bingads-webui-universal/temporal-utitlities';
import { getThisMonday } from './date-util';

export const RANGES = {
  Yesterday: 1,
  Last7Days: 2,
  Today: 3,
  Custom: 4,
  Last30Days: 5,
  Last14Days: 6,
  ThisWeek: 7,
  ThisWeekMon: 8,
  LastWeek: 9,
  ThisMonth: 10,
  LastMonth: 11,
  Last3Months: 12,
  Last6Months: 13,
  LastWeekMon: 14,
  ThisYear: 15,
  LastYear: 16,
  EntireTimeLast18Months: 17,
  EntireTimeLast5Years: 18,
  EntireTime: 22,
};

const i18nMap = {
  Today: _TL_('Today'),
  Custom: _TL_('Custom'),
  Yesterday: _TL_('Yesterday'),
  ThisWeek: _TL_('This week (Sun - Today)'),
  ThisWeekMon: _TL_('This week (Mon - Today)'),
  Last7Days: _TL_('Last 7 days'),
  LastWeek: _TL_('Last week (Sun - Sat)'),
  LastWeekMon: _TL_('Last week (Mon - Sun)'),
  Last14Days: _TL_('Last 14 days'),
  ThisMonth: _TL_('This month'),
  LastMonth: _TL_('Last month'),
  Last30Days: _TL_('Last 30 days'),
  Last3Months: _TL_('Last 3 months'),
  Last6Months: _TL_('Last 6 months'),
  LastYear: _TL_('Last year'),
  LastPeriod: _TL_('Last period'),
  ThisYear: _TL_('This year'),
  EntireTime: _TL_('Entire time'),
};

export const getEntireTime = (i18n, range) => ({
  rangeId: RANGES.EntireTime,
  key: 'EntireTime',
  text: i18n.getString(i18nMap.EntireTime),
  range,
});

export const getAllDateRanges = ({
  i18n,
  timeZone = 'US/Pacific',
  minDate,
  maxDate,
}) => {
  const todayObj = i18n.getToday({ timeZone });
  const today = new CivilDate(todayObj.year, todayObj.month, todayObj.day);
  const yesterday = today.plus({ days: -1 });
  const thisSunday = today.plus({ days: -getCivilDateDayOfWeek(today) });
  const thisMonday = getThisMonday(today);
  const last7day = today.plus({ days: -7 });
  const last14day = today.plus({ days: -14 });
  const last30day = today.plus({ days: -30 });
  const lastMonday = today.plus({ days: -getCivilDateDayOfWeek(today) - 6 });
  const lastSunday = today.plus({ days: -getCivilDateDayOfWeek(today) - 7 });
  const lastSaturday = today.plus({ days: -getCivilDateDayOfWeek(today) - 1 });
  const thisMonthFirstDay = getCivilDateMonthStart(today);
  const lastMonthlastDay = thisMonthFirstDay.plus({ days: -1 });
  const last3MonthsFirstDay = thisMonthFirstDay.plus({ months: -3 });
  const last6MonthsFirstDay = thisMonthFirstDay.plus({ months: -6 });
  const last18MonthsFirstDay = thisMonthFirstDay.plus({ years: -1, months: -6 });
  const lastMonthFirstDay = getCivilDateMonthStart(lastMonthlastDay);
  const thisYearFirstDay = new CivilDate(today.year, 1, 1);
  const lastYearFirstDay = thisYearFirstDay.plus({ years: -1 });
  const lastYearLastDay = thisYearFirstDay.plus({ days: -1 });
  const last5Years = today.plus({ years: -5 });

  const minSupportDay = minDate || today.plus({ years: -3 });
  const maxSupportDay = maxDate || today;

  return [
    {
      rangeId: RANGES.Custom, key: 'Custom', text: i18n.getString(i18nMap.Custom), range: { start: undefined, end: undefined },
    },
    {
      rangeId: RANGES.Today, key: 'Today', text: i18n.getString(i18nMap.Today), range: { start: today, end: today },
    },
    {
      rangeId: RANGES.Yesterday, key: 'Yesterday', text: i18n.getString(i18nMap.Yesterday), range: { start: yesterday, end: yesterday },
    },
    {
      rangeId: RANGES.ThisWeek, key: 'ThisWeek', text: i18n.getString(i18nMap.ThisWeek), range: { start: thisSunday, end: today },
    },
    {
      rangeId: RANGES.ThisWeekMon, key: 'ThisWeekMon', text: i18n.getString(i18nMap.ThisWeekMon), range: { start: thisMonday, end: today },
    },
    {
      rangeId: RANGES.Last7Days, key: 'Last7Days', text: i18n.getString(i18nMap.Last7Days), range: { start: last7day, end: yesterday },
    },
    {
      rangeId: RANGES.LastWeek, key: 'LastWeek', text: i18n.getString(i18nMap.LastWeek), range: { start: lastSunday, end: lastSaturday },
    },
    {
      rangeId: RANGES.LastWeekMon, key: 'LastWeekMon', text: i18n.getString(i18nMap.LastWeekMon), range: { start: lastMonday, end: thisSunday },
    },
    {
      rangeId: RANGES.Last14Days, key: 'Last14Days', text: i18n.getString(i18nMap.Last14Days), range: { start: last14day, end: yesterday },
    },
    {
      rangeId: RANGES.ThisMonth, key: 'ThisMonth', text: i18n.getString(i18nMap.ThisMonth), range: { start: thisMonthFirstDay, end: today },
    },
    {
      rangeId: RANGES.LastMonth, key: 'LastMonth', text: i18n.getString(i18nMap.LastMonth), range: { start: lastMonthFirstDay, end: lastMonthlastDay },
    },
    {
      rangeId: RANGES.Last30Days, key: 'Last30Days', text: i18n.getString(i18nMap.Last30Days), range: { start: last30day, end: yesterday },
    },
    {
      rangeId: RANGES.Last3Months, key: 'Last3Months', text: i18n.getString(i18nMap.Last3Months), range: { start: last3MonthsFirstDay, end: lastMonthlastDay },
    },
    {
      rangeId: RANGES.Last6Months, key: 'Last6Months', text: i18n.getString(i18nMap.Last6Months), range: { start: last6MonthsFirstDay, end: lastMonthlastDay },
    },
    {
      rangeId: RANGES.ThisYear, key: 'ThisYear', text: i18n.getString(i18nMap.ThisYear), range: { start: thisYearFirstDay, end: today },
    },
    {
      rangeId: RANGES.LastYear, key: 'LastYear', text: i18n.getString(i18nMap.LastYear), range: { start: lastYearFirstDay, end: lastYearLastDay },
    },
    {
      rangeId: RANGES.EntireTimeLast18Months, key: 'EntireTimeLast18Months', text: i18n.getString(i18nMap.EntireTime), range: { start: last18MonthsFirstDay, end: today },
    },
    {
      rangeId: RANGES.EntireTimeLast5Years, key: 'EntireTimeLast5Years', text: i18n.getString(i18nMap.EntireTime), range: { start: last5Years, end: today },
    },
    getEntireTime(i18n, { start: minSupportDay, end: maxSupportDay }),
  ].map((x) => {
    const { range } = x;
    if (range.start < minSupportDay) {
      range.start = minSupportDay;
    }
    if (range.end < minSupportDay) {
      range.end = minSupportDay;
    }
    return x;
  });
};

export const getSpecificDateRanges = ({
  i18n,
  timeZone = 'US/Pacific',
  minDate,
  maxDate,
  rangeIds,
}) => getAllDateRanges({
  i18n,
  timeZone,
  minDate,
  maxDate,
}).filter(({ rangeId }) => rangeIds.indexOf(rangeId) > -1);
